import { useQuery } from '@tanstack/react-query'
import {
  LineChart, Line, BarChart, Bar, Cell, PieChart, Pie,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts'
import { adminApi } from '@/api/admin'
import { KpiCard, EmptyChart, SectionTitle } from './AnalyticsShared'
import { CHART_COLORS, useAnalyticsMoney } from './analyticsHelpers'

export function FinanceTab({ from, to }: { from: string | null; to: string | null }) {
  const { fmtMoney, fmtMoneyShort } = useAnalyticsMoney()
  const { data, isLoading } = useQuery({
    queryKey: ['analytics-finance', from, to],
    queryFn: () => adminApi.getFinanceAnalytics(from, to).then(r => r.data),
  })

  if (isLoading) return <p className="text-gray-400 text-sm py-8">Loading…</p>
  if (!data) return null

  const totalInvoices = data.paidInvoices + data.overdueInvoices + data.pendingInvoices
  const pieData = [
    { name: 'Paid',    value: data.paidInvoices,    color: '#22c55e' },
    { name: 'Overdue', value: data.overdueInvoices, color: '#ef4444' },
    { name: 'Pending', value: data.pendingInvoices, color: '#f59e0b' },
  ].filter(d => d.value > 0)

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KpiCard label="Total revenue"     value={fmtMoney(data.totalRevenue)} accent />
        <KpiCard label="Outstanding"       value={fmtMoney(data.totalOutstanding)} />
        <KpiCard label="Avg invoice value" value={fmtMoney(data.averageInvoiceValue)} />
        <KpiCard label="Invoices"          value={totalInvoices.toString()}
          sub={`${data.paidInvoices} paid · ${data.overdueInvoices} overdue`} />
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
        <SectionTitle>Monthly revenue</SectionTitle>
        <div className="mt-4 h-64">
          {data.monthlyRevenue.length === 0 ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.monthlyRevenue} margin={{ right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="monthLabel" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={fmtMoneyShort} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => [fmtMoney(Number(v)), 'Revenue']} />
                <Line
                  type="monotone" dataKey="revenue" stroke="#6366f1" strokeWidth={2.5}
                  dot={{ fill: '#6366f1', r: 4 }} activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <SectionTitle>Invoice status</SectionTitle>
          <div className="mt-4 h-48">
            {pieData.length === 0 ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={3} dataKey="value">
                    {pieData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Tooltip formatter={(v, _, e) => [Number(v), (e as { payload: { name: string } }).payload.name]} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="mt-2 grid grid-cols-3 gap-2 text-center text-xs">
            <div>
              <p className="text-2xl font-bold text-green-600">{data.paidInvoices}</p>
              <p className="text-gray-500 mt-0.5">Paid</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-red-500">{data.overdueInvoices}</p>
              <p className="text-gray-500 mt-0.5">Overdue</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-500">{data.pendingInvoices}</p>
              <p className="text-gray-500 mt-0.5">Pending</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <SectionTitle>Top courses by revenue</SectionTitle>
          <div className="mt-4 h-56">
            {data.topCoursesByRevenue.length === 0 ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.topCoursesByRevenue} layout="vertical" margin={{ left: 8, right: 24 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tickFormatter={fmtMoneyShort} tick={{ fontSize: 11 }} />
                  <YAxis
                    type="category" dataKey="courseName" width={110}
                    tickFormatter={(s: string) => s.length > 14 ? s.slice(0, 14) + '…' : s}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip formatter={(v) => [fmtMoney(Number(v)), 'Revenue']} />
                  <Bar dataKey="revenue" radius={[0, 4, 4, 0]}>
                    {data.topCoursesByRevenue.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {data.monthlyRevenue.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Month</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Invoices</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Revenue</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {[...data.monthlyRevenue].reverse().map(m => (
                <tr key={`${m.year}-${m.month}`} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-gray-700">{m.monthLabel}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-gray-600">{m.invoiceCount}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold text-gray-900">{fmtMoney(m.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
